import { getActiveCompanyConfig } from "domain/company/companyConfig";
import { calculateQuoteDraft } from "domain/quote/calculateQuote";
import { QuoteInputDraft } from "domain/quote/types";

import { useEffect, useMemo, useState } from "react";

import { useFetchQuotationContext } from "context/QuotationListContext";
import { Link } from "react-router-dom";
import { MaterialType, Province, QuotationFireStoreFields } from "types/canvasTypes";

type Period = "week" | "month";

type PeriodStats = {
  count: number;
  revenue: number;
  emails: Set<string>;
};

type BreakdownEntry = {
  label: string;
  count: number;
};

type DashboardStats = {
  current: PeriodStats;
  previous: PeriodStats;
  byProvince: BreakdownEntry[];
  byMaterial: BreakdownEntry[];
  byHeight: BreakdownEntry[];
  recent: QuotationFireStoreFields[];
};

function getWeekBounds(date: Date): { start: Date; end: Date } {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const start = new Date(d);
  start.setDate(d.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return { start, end };
}

function getMonthBounds(date: Date): { start: Date; end: Date } {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  return { start, end };
}

function getPeriodBounds(period: Period): {
  currentStart: Date;
  currentEnd: Date;
  previousStart: Date;
  previousEnd: Date;
} {
  const now = new Date();
  if (period === "week") {
    const current = getWeekBounds(now);
    const previousStart = new Date(current.start);
    previousStart.setDate(previousStart.getDate() - 7);
    return {
      currentStart: current.start,
      currentEnd: current.end,
      previousStart,
      previousEnd: current.start,
    };
  }
  const current = getMonthBounds(now);
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const previous = getMonthBounds(prevDate);
  return {
    currentStart: current.start,
    currentEnd: current.end,
    previousStart: previous.start,
    previousEnd: previous.end,
  };
}

function getQuotationCost(q: QuotationFireStoreFields): number {
  try {
    const lines = JSON.parse(q.lines);
    if (!Array.isArray(lines) || lines.length === 0) return 0;

    const input: QuoteInputDraft = {
      lines,
      glassHeight: Number(q.selectedLength) as 36 | 42 | 48,
      scale: q.scale as 2.9 | 1.5 | 1,
      province: q.province as Province,
      materialType: q.material as MaterialType,
      includeAttachments: q.includeAttachement,
      chalkLine: q.chalkLine,
      gapSize: q.gapSize ?? 1,
      hasDoubleDoor: false,
    };

    const config = getActiveCompanyConfig();
    const result = calculateQuoteDraft(input, config);
    return result.totalCost;
  } catch {
    return 0;
  }
}

function computeDashboardStats(
  quotations: QuotationFireStoreFields[],
  period: Period
): DashboardStats {
  const bounds = getPeriodBounds(period);

  const current: PeriodStats = { count: 0, revenue: 0, emails: new Set() };
  const previous: PeriodStats = { count: 0, revenue: 0, emails: new Set() };

  const provinceCounts: Record<string, number> = {};
  const materialCounts: Record<string, number> = {};
  const heightCounts: Record<string, number> = {};

  const currentPeriodQuotations: QuotationFireStoreFields[] = [];

  for (const q of quotations) {
    const qDate = new Date(q.date.seconds * 1000);
    const cost = getQuotationCost(q);

    if (qDate >= bounds.currentStart && qDate < bounds.currentEnd) {
      current.count++;
      current.revenue += cost;
      if (q.email) current.emails.add(q.email.toLowerCase());
      currentPeriodQuotations.push(q);

      const prov = q.province === Province.Quebec ? "Quebec" : "Ontario";
      provinceCounts[prov] = (provinceCounts[prov] || 0) + 1;

      const mat = q.material === MaterialType.Black ? "Black" : "Silver";
      materialCounts[mat] = (materialCounts[mat] || 0) + 1;

      const height = `${q.selectedLength}"`;
      heightCounts[height] = (heightCounts[height] || 0) + 1;
    } else if (qDate >= bounds.previousStart && qDate < bounds.previousEnd) {
      previous.count++;
      previous.revenue += cost;
      if (q.email) previous.emails.add(q.email.toLowerCase());
    }
  }

  const toEntries = (counts: Record<string, number>): BreakdownEntry[] =>
    Object.entries(counts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

  return {
    current,
    previous,
    byProvince: toEntries(provinceCounts),
    byMaterial: toEntries(materialCounts),
    byHeight: toEntries(heightCounts),
    recent: currentPeriodQuotations.slice(0, 5),
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}

function ChangeIndicator({
  current,
  previous,
  period,
}: {
  current: number;
  previous: number;
  period: Period;
}) {
  const change = percentChange(current, previous);
  const label = period === "week" ? "vs last week" : "vs last month";
  if (change === null) {
    return (
      <span className="admin-stat-card__change admin-stat-card__change--neutral">
        — {label}
      </span>
    );
  }
  const isUp = change >= 0;
  const cls = isUp
    ? "admin-stat-card__change--up"
    : "admin-stat-card__change--down";
  const arrow = isUp ? "\u2191" : "\u2193";
  return (
    <span className={`admin-stat-card__change ${cls}`}>
      {arrow} {Math.abs(Math.round(change))}% {label}
    </span>
  );
}

function BreakdownBar({
  entries,
  title,
}: {
  entries: BreakdownEntry[];
  title: string;
}) {
  const total = entries.reduce((s, e) => s + e.count, 0);
  return (
    <div className="admin-breakdown-card">
      <h3 className="admin-breakdown-card__title">{title}</h3>
      {entries.length === 0 ? (
        <p className="admin-stat-card__change admin-stat-card__change--neutral">
          No data
        </p>
      ) : (
        entries.map((entry) => {
          const pct = total > 0 ? (entry.count / total) * 100 : 0;
          return (
            <div key={entry.label} className="admin-breakdown-item">
              <div className="admin-breakdown-bar__label">
                <span>{entry.label}</span>
                <span className="admin-breakdown-bar__count">
                  {entry.count}
                </span>
              </div>
              <div className="admin-breakdown-bar">
                <div
                  className="admin-breakdown-bar__fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

const PERIOD_LABELS: Record<Period, string> = {
  week: "This Week",
  month: "This Month",
};

const AdminDashboard = () => {
  const { getQuotations, loading, quotations } = useFetchQuotationContext();
  const [period, setPeriod] = useState<Period>("week");

  useEffect(() => {
    getQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(
    () => computeDashboardStats(quotations, period),
    [quotations, period]
  );

  if (loading && quotations.length === 0) {
    return (
      <div>
        <div className="admin-page-header">
          <h1>Dashboard</h1>
        </div>
        <div className="admin-empty-state">Loading dashboard...</div>
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div>
        <div className="admin-page-header">
          <h1>Dashboard</h1>
        </div>
        <div className="admin-empty-state">
          No quotations yet. Data will appear here once quotations are created.
        </div>
      </div>
    );
  }

  const avgCurrent =
    stats.current.count > 0
      ? stats.current.revenue / stats.current.count
      : 0;
  const avgPrevious =
    stats.previous.count > 0
      ? stats.previous.revenue / stats.previous.count
      : 0;

  return (
    <div>
      <div className="admin-page-header">
        <div className="admin-page-header__left">
          <h1>Dashboard</h1>
          <span className="admin-badge">{quotations.length} total</span>
        </div>
        <div className="admin-tabs" style={{ marginBottom: 0, borderBottom: "none" }}>
          {(["week", "month"] as Period[]).map((p) => (
            <button
              key={p}
              className={`admin-tab${period === p ? " admin-tab--active" : ""}`}
              onClick={() => setPeriod(p)}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Row 1 — KPI Stat Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <p className="admin-stat-card__label">Quotes {PERIOD_LABELS[period]}</p>
          <p className="admin-stat-card__value">{stats.current.count}</p>
          <ChangeIndicator
            current={stats.current.count}
            previous={stats.previous.count}
            period={period}
          />
        </div>
        <div className="admin-stat-card">
          <p className="admin-stat-card__label">Estimated Revenue</p>
          <p className="admin-stat-card__value">
            {formatCurrency(stats.current.revenue)}
          </p>
          <ChangeIndicator
            current={stats.current.revenue}
            previous={stats.previous.revenue}
            period={period}
          />
        </div>
        <div className="admin-stat-card">
          <p className="admin-stat-card__label">Average Quote Value</p>
          <p className="admin-stat-card__value">{formatCurrency(avgCurrent)}</p>
          <ChangeIndicator current={avgCurrent} previous={avgPrevious} period={period} />
        </div>
        <div className="admin-stat-card">
          <p className="admin-stat-card__label">Unique Customers</p>
          <p className="admin-stat-card__value">
            {stats.current.emails.size}
          </p>
          <ChangeIndicator
            current={stats.current.emails.size}
            previous={stats.previous.emails.size}
            period={period}
          />
        </div>
      </div>

      {/* Row 2 — Breakdown Cards */}
      <div className="admin-breakdown-grid">
        <BreakdownBar entries={stats.byProvince} title="By Province" />
        <BreakdownBar entries={stats.byMaterial} title="By Material" />
        <BreakdownBar entries={stats.byHeight} title="By Height" />
      </div>

      {/* Row 3 — Recent Quotations */}
      <div className="admin-card">
        <h3 className="admin-card__title">Recent Quotations</h3>
        {stats.recent.length === 0 ? (
          <p className="admin-stat-card__change admin-stat-card__change--neutral">
            No quotations {period === "week" ? "this week" : "this month"}
          </p>
        ) : (
          <div className="admin-table-wrap">
            <table className="quotation-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Date</th>
                  <th>Province</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((q) => (
                  <tr key={q.id}>
                    <td>
                      <Link to={`/admin/quotations/${q.id}`}>
                        {q.name || "No name"}
                      </Link>
                    </td>
                    <td className="admin-cell-truncate">{q.email}</td>
                    <td>
                      {new Date(q.date.seconds * 1000).toLocaleDateString(
                        "en-CA"
                      )}
                    </td>
                    <td>
                      <span className="admin-province-badge">
                        {q.province === Province.Quebec ? "QC" : "ON"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
